import { useMemo, useState } from 'react';

import { ApiClient, formatDateTime, useAsync } from '@clientma/core';
import { Alert, Button, Card, Input, Skeleton, Stat } from '@clientma/ui';

import { providerApi } from '../api';

/**
 * What SJS Tech can ask about its own business.
 *
 * Commercial facts only — subscriptions, invoices, tickets, credits. Nothing here reads a
 * customer's own records, and the control plane's licence to cross every tenant depends on it
 * staying that way.
 */
export default function Reports({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const catalogue = useAsync((signal) => provider.reportCatalogue(signal), [provider]);

  const [selected, setSelected] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const report = useAsync(
    (signal) =>
      selected
        ? provider.runReport({ key: selected, from: from || undefined, to: to || undefined }, signal)
        : Promise.resolve(null),
    [provider, selected, from, to],
  );

  const groups = useMemo(() => {
    const map = new Map<string, typeof catalogue.data>();

    for (const definition of catalogue.data ?? []) {
      const list = map.get(definition.group) ?? [];
      map.set(definition.group, [...(list ?? []), definition]);
    }

    return [...map.entries()];
  }, [catalogue.data]);

  return (
    <div className="cma-stack">
      {catalogue.error && (
        <Alert tone="danger" title="The reports could not be listed">
          {catalogue.error.message}
        </Alert>
      )}

      {catalogue.isLoading && <Skeleton height={140} />}

      {!selected && (
        <div className="cma-stack">
          {groups.map(([group, definitions]) => (
            <div key={group} className="cma-stack cma-stack--tight">
              <div className="provider-section">{group}</div>

              <div className="provider-report-grid">
                {(definitions ?? []).map((definition) => (
                  <button
                    key={definition.key}
                    type="button"
                    className="provider-report-card"
                    onClick={() => setSelected(definition.key)}
                  >
                    <strong>{definition.name}</strong>
                    <span className="provider-muted">{definition.description}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <>
          <div className="provider-toolbar">
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              ← All reports
            </Button>

            <Input
              type="date"
              value={from}
              aria-label="From"
              onChange={(event) => setFrom(event.target.value)}
            />
            <Input
              type="date"
              value={to}
              aria-label="To"
              onChange={(event) => setTo(event.target.value)}
            />
          </div>

          {report.error && (
            <Alert tone="danger" title="The report could not be run">
              {report.error.message}
            </Alert>
          )}

          {report.isLoading && <Skeleton height={280} />}

          {report.data && (
            <Card title={report.data.name}>
              {report.data.stats.length > 0 && (
                <div className="cma-stat-row">
                  {report.data.stats.map((stat) => (
                    <Stat
                      key={stat.label}
                      label={stat.label}
                      value={stat.value}
                      meta={stat.meta ?? undefined}
                    />
                  ))}
                </div>
              )}

              {report.data.rows.length === 0 ? (
                <p className="provider-muted">Nothing in this period.</p>
              ) : (
                // Its own scroller. A wide report must not make the whole console scroll
                // sideways — the navigation goes with it and the page stops feeling like one.
                <div className="provider-report-scroll">
                  <table className="provider-report-table">
                    <thead>
                      <tr>
                        {report.data.columns.map((column, index) => (
                          <th
                            key={column}
                            className={report.data!.isNumeric[index] ? 'provider-num' : undefined}
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.data.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, index) => (
                            <td
                              key={index}
                              className={report.data!.isNumeric[index] ? 'provider-num' : undefined}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="provider-muted">
                {report.data.rows.length} row{report.data.rows.length === 1 ? '' : 's'} ·{' '}
                {report.data.from} to {report.data.to} · run{' '}
                {formatDateTime(report.data.ranAtUtc, 'en-SG')}
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
